<template>
  <v-dialog
    v-model="show"
    persistent
    @keydown.esc="done"
    width="1000"
    v-if="role('admin', 'safetyRepresentative')"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        v-on="on"
        v-if="role('admin', 'safetyRepresentative')"
        class="primary ml-3"
      >
        <v-icon dark left>person_add</v-icon>
        {{ $t("import.importManual") }}
      </v-btn>
    </template>

    <v-card :loading="$apollo.loading || loading">
      <v-card-title>
        <span class="headline">{{ $t("import.importManual") }}</span>
      </v-card-title>
      <v-card-text class="pb-0">
        <v-container grid-list-md>
          <v-form ref="importManualForm" v-model="valid" lazy-validation>
            <v-btn
              @click="getNewTrialDayEmployeeId"
              color="primary"
              :loading="$apollo.loading || loading"
              >{{ $t("import.newTrialDayEmployeeId") }}</v-btn
            >
            <v-row justify="center" class="mx-6">
              <template v-if="!valid">
                <div class="pb-2">
                  <v-icon color="error" left>warning</v-icon>
                  <span class="error--text">{{
                    $t("import.requiredAlert")
                  }}</span>
                </div>
              </template>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  :class="employee.existing ? 'mb-4' : ''"
                  v-model="employee.id"
                  label="ID"
                  @change="employeeSelected"
                  :rules="[rules.number, rules.isActive]"
                  required
                  persistent-hint
                  :hint="getEmployeeIdHint(employee)"
                />
                <v-text-field
                  v-model="employee.employeeId"
                  :label="$t('import.employeeId')"
                  :rules="[rules.required]"
                  required
                  :hint="
                    isDuplicateEmployeeId
                      ? $t('profile.personalInformation.employeeExists')
                      : ''
                  "
                  persistent-hint
                  class="orange-background-hint"
                />
                <v-text-field
                  v-model="employee.firstName"
                  :label="$t('import.firstName')"
                  :rules="[rules.required]"
                  required
                />
                <v-text-field
                  v-model="employee.lastName"
                  :label="$t('import.lastName')"
                  :rules="[rules.required]"
                  required
                />
              </v-col>
              <v-col>
                <autocomplete-tag-subtitle
                  v-model="employee.location"
                  :items="locationTags"
                  :label="$t('tags.location')"
                  item-value="name"
                  :rules="[rules.required]"
                  required
                />
                <autocomplete-tag-subtitle
                  v-model="employee.department"
                  :items="departmentTags"
                  :label="$t('tags.department')"
                  item-value="name"
                  :rules="[rules.required]"
                  required
                />
                <autocomplete-tag-subtitle
                  v-model="employee.collar"
                  :items="collarTags"
                  :label="$t('tags.collar')"
                  item-value="name"
                  :rules="[rules.requiredCollarArray]"
                  multiple
                  required
                />
                <v-autocomplete
                  v-model="employee.modeIds"
                  :items="modes"
                  :label="$t('profile.assignment.modes.dialog')"
                  item-value="id"
                  item-text="name"
                  multiple
                  deletable-chips
                />
                <v-autocomplete
                  v-model="employee.manager"
                  :items="activeEmployees"
                  :label="$t('profile.manager')"
                  :item-text="(e) => `${$options.filters.nameId(e)}`"
                  :rules="[rules.requiredIfMentorship]"
                  return-object
                />
              </v-col>
              <v-col class="d-flex flex-column">
                <div
                  v-if="
                    employee.personalInformation &&
                      ((role('admin') && !employee.existing) ||
                        role('safetyRepresentative'))
                  "
                >
                  <v-text-field
                    v-model="employee.personalInformation.personalCode"
                    :label="$t('profile.personalInformation.personalCode')"
                    :rules="[rules.required]"
                    required
                    :hint="
                      isDuplicatePersonalCode
                        ? $t('profile.personalInformation.duplicateWarning')
                        : ''
                    "
                    persistent-hint
                    class="orange-background-hint"
                  />
                  <v-text-field
                    v-model="employee.personalInformation.address"
                    :label="$t('profile.personalInformation.address')"
                  />
                  <v-text-field
                    v-model="employee.personalInformation.phone"
                    :label="$t('profile.personalInformation.phone')"
                  />
                </div>
                <div class="mt-auto mb-1">
                  <v-checkbox
                    v-model="startLocationMentorship"
                    :label="$t('import.startLocationMentorship')"
                  />
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="done">
          {{ $t("dialog.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="$apollo.loading || loading"
          text
          @click="importEmployee"
          >{{
            employee.existing
              ? employee.isActive
                ? $t("dialog.change")
                : $t("import.activate")
              : $t("import.add")
          }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

import AllEmployeesQuery from "@/graphql/queries/employee/AllEmployees.gql";
import ImportQuery from "@/graphql/ImportManual.gql";
import TagsQuery from "@/graphql/Tags.gql";
import ModesQuery from "@/graphql/queries/mode/Modes.gql";
import NewTrialDayEmployeeIdQuery from "@/graphql/queries/import/NewTrialDayEmployeeId.gql";

import AutocompleteTagSubtitle from "@/components/control/AutocompleteTagSubtitle.vue";

export default {
  components: {
    AutocompleteTagSubtitle,
  },
  data() {
    return {
      show: false,
      loading: false,
      employee: this.initEmployee,
      allTags: [],
      locationTags: [],
      departmentTags: [],
      collarTags: [],
      valid: false,
      startLocationMentorship: true,
      rules: {
        number: (value) => {
          return (
            /^\d+$/.test(value.id || value) ||
            this.$i18n.t("validation.numeric")
          );
        },
        isActive: (value) => {
          if (!value) return true;
          const e = this.employeesAll.find((_e) => _e.id === value);
          if (!e) {
            return true;
          } else {
            return !e.isActive || this.$i18n.t("validation.employeeExists");
          }
        },
        required: (value) => !!value || this.$i18n.t("validation.required"),
        requiredCollarArray: (value) =>
          (this.collarTags && this.collarTags.length == 0) ||
          (Array.isArray(value) && !!value.length) ||
          this.$i18n.t("validation.required"),
        requiredIfMentorship: (value) =>
          !this.startLocationMentorship ||
          !!value ||
          this.$i18n.t("import.requiredIfMentorship"),
      },
    };
  },

  mounted() {
    this.initEmployee();
  },

  computed: {
    ...mapGetters(["role"]),
    isDuplicatePersonalCode() {
      return (
        this.employeesAll &&
        this.employee.personalInformation.personalCode &&
        this.employeesAll.some(
          (e) =>
            e.id !== this.employee.id &&
            e.personalInformation.personalCode ===
              this.employee.personalInformation.personalCode
        )
      );
    },
    isDuplicateEmployeeId() {
      return (
        this.employeesAll &&
        this.employee.employeeId &&
        this.employeesAll.some(
          (e) =>
            e.id !== this.employee.id &&
            e.employeeId === this.employee.employeeId
        )
      );
    },
    activeEmployees() {
      return this.employeesAll
        ? this.employeesAll.filter((e) => e.isActive)
        : [];
    },
  },

  methods: {
    getNewTrialDayEmployeeId() {
      this.$apollo
        .query({
          query: NewTrialDayEmployeeIdQuery,
        })
        .then((res) => {
          this.employee.id = res.data.newTrialDayEmployeeId;
          this.employee.employeeId = res.data.newTrialDayEmployeeId;
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            color: "error",
            text: err,
          });
        });
    },
    getEmployeeIdHint(employee) {
      if (!employee) {
        return "";
      }

      if (employee.existing) {
        return this.$t("import.inactiveExists");
      }

      return "";
    },
    isValidRegex(regexStr) {
      if (!regexStr) {
        return false;
      }

      try {
        new RegExp(regexStr);
        return true;
      } catch {
        return false;
      }
    },
    employeeSelected(sel) {
      // try to find existing employee by code
      if (typeof sel === "string") {
        sel = this.employeesAll.find((_e) => _e.id === sel) || sel;
      }
      const getTagName = (categoryName) => {
        const item = sel.tags.find((t) => t.category === categoryName);
        return item ? item.name : "";
      };

      // existing employee selected
      if (typeof sel === "object") {
        sel.tags = sel.tags.filter((t) =>
          this.allTags.some((at) => at.id === t.id)
        );
        Object.assign(this.employee, {
          existing: sel,
          id: sel.id,
          employeeId: sel.employeeId,
          firstName: sel.firstName,
          lastName: sel.lastName,
          isActive: sel.isActive,
          location: getTagName("location"),
          department: getTagName("department"),
          collar: sel.tags
            .filter((t) => t.category === "collar")
            .map((t) => t.name),
          manager: sel.manager,
          personalInformation: sel.personalInformation || {
            personalCode: "",
            address: "",
            phone: "",
          },
        });
      }

      // non-existing employee code entered
      else if (this.employee.existing) {
        Object.assign(this.employee, {
          existing: null,
          employeeId: "",
          firstName: "",
          lastName: "",
          location: "",
          department: "",
          collar: [],
          isActive: false,
          manager: null,
          personalInformation: {
            personalCode: "",
            address: "",
            phone: "",
          },
        });
        this.$refs.importManualForm.resetValidation();
      }
    },

    importEmployee() {
      if (!this.$refs.importManualForm.validate()) {
        return;
      }
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: ImportQuery,
          variables: {
            e: {
              id: this.employee.id,
              employeeId: this.employee.employeeId,
              firstName: this.employee.firstName,
              lastName: this.employee.lastName,
              modeIds: Array.isArray(this.employee.modeIds)
                ? this.employee.modeIds
                : [this.employee.modeIds],
              tags: [
                {
                  category: "location",
                  name: this.employee.location,
                },
                {
                  category: "department",
                  name: this.employee.department,
                },
                ...this.employee.collar.map((name) => ({
                  category: "collar",
                  name,
                })),
              ],
              managerId: this.employee.manager
                ? this.employee.manager.id
                : null,
              personalInformation: this.employee.personalInformation,
              startLocationMentorship: this.startLocationMentorship,
            },
          },
        })
        .then((resp) => {
          this.loading = false;

          this.$emit("importResult", resp.data.importManual);
          this.done();
        })
        .catch((err) => {
          this.loading = false;
          this.$emit("importError", err);
          this.done();
        });
    },

    initEmployee() {
      this.employee = {
        id: "",
        employeeId: "",
        firstName: "",
        lastName: "",
        location: "",
        department: "",
        collar: [],
        modeIds: [],
        personalInformation: {
          personalCode: "",
          address: "",
          phone: "",
        },
      };
      this.startLocationMentorship = true;
    },

    done() {
      this.$refs.importManualForm.resetValidation();
      this.initEmployee();
      this.show = false;
    },
  },

  apollo: {
    employeesAll: {
      query: AllEmployeesQuery,
      fetchPolicy: "cache-and-network",
      skip() {
        return !this.show;
      },
    },
    modes: {
      query: ModesQuery,
      fetchPolicy: "cache-and-network",
      skip() {
        return !this.show;
      },
    },
    tags: {
      query: TagsQuery,
      fetchPolicy: "cache-and-network",
      update(data) {
        this.allTags = data.tags;
        this.locationTags = data.tags.filter((t) => t.category === "location");
        this.departmentTags = data.tags.filter(
          (t) => t.category === "department"
        );
        this.collarTags = data.tags.filter((t) => t.category === "collar");
      },
      skip() {
        return !this.show;
      },
    },
  },
};
</script>

<style>
.orange-background-hint .v-messages__message {
  color: white !important;
  background-color: #fb8c00 !important;
  padding: 10px 13px;
  border-radius: 5px;
  max-width: fit-content;
}
.orange-background-hint .error--text .v-messages__message {
  color: #ff5252 !important;
  background-color: transparent !important;
  padding: 0;
  border-radius: 0;
  max-width: none;
}
</style>
